import { Button } from '@/components/ui/Button';
import { useExperienceStore } from '@/lib/stores/experience-store';
import { HOME_HERO_VIDEO, HOME_HERO_POSTER, homeHeroPosterAttribute } from '@/lib/homeHeroPoster';
import { toast } from '@/lib/utils/toast';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation,useNavigate } from 'react-router-dom';

export function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadExperiences } = useExperienceStore();

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  // Handle success message from password reset
  useEffect(() => {
    const state = location.state as { message?: string };
    if (state?.message) {
      toast.success(state.message);
      // Clear the state to prevent showing message again on refresh
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  return (
    <div className="relative">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-[100svh] flex flex-col" data-testid="home-hero">
        {/* Background Video */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            {...homeHeroPosterAttribute(HOME_HERO_POSTER)}
          >
            <source src={HOME_HERO_VIDEO} type="video/mp4" />
          </video>
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/90" />
        </div>
        
        {/* Hero Content */}
        <div className="relative container mx-auto px-4 flex-1 flex items-center">
          <div className="max-w-2xl">
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-brand-display font-light mb-4 sm:mb-8 text-foreground leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Discover Through Trust
            </motion.h1>
            <motion.p 
              className="text-lg sm:text-xl text-foreground mb-8 sm:mb-12 font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              No noise—just places worth showing up for.
            </motion.p>
            <motion.div 
              className="flex gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/about')}
                className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator - removed since no content below */}
      </section>

      {/* Past nights — quiet proof, not promises */}
      <section className="bg-background py-16 sm:py-24" data-testid="home-past-nights">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs font-medium uppercase tracking-[0.35em] text-muted-foreground">
            Past nights
          </p>
          <h2 className="mx-auto mt-4 max-w-2xl text-center font-brand-display text-3xl sm:text-4xl font-light leading-tight text-foreground">
            Some things happen in person or not at all.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <figure className="relative h-64 sm:h-80 overflow-hidden">
              <img
                src="https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(27).jpg"
                alt="The full group gathered in front of an Indian palace"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em] text-white/90">
                India, the whole group
              </figcaption>
            </figure>
            <figure className="relative h-64 sm:h-80 overflow-hidden">
              <img
                src="https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(3).jpg"
                alt="Friends gathered around a candlelit dinner table at night"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em] text-white/90">
                The long table, by candlelight
              </figcaption>
            </figure>
            <figure className="relative h-64 sm:h-80 overflow-hidden">
              <img
                src="https://rendezvouswebsite.b-cdn.net/experience%20carousel/Carousel%20Media%20(8).jpg"
                alt="Friends on a boat at sunset over the water"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em] text-white/90">
                Sunset, on the water
              </figcaption>
            </figure>
          </div>
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => navigate('/about')}
              className="inline-flex items-center text-base text-foreground/80 hover:text-foreground transition-colors"
            >
              Our story
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
